import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function ChatDialog({ open, onOpenChange, poolId, guestId = null, title = "" }) {
  const { t, user } = useApp();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    if (!poolId) return;
    const params = user?.role === "owner" ? { guest_id: guestId } : {};
    try {
      const { data } = await api.get(`/messages/${poolId}`, { params });
      setMessages(data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch {}
  };

  useEffect(() => {
    if (!open) return;
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [open, poolId, guestId]);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      if (user?.role === "owner") {
        await api.post("/messages/reply", { pool_id: poolId, guest_id: guestId, body });
      } else {
        await api.post("/messages", { pool_id: poolId, body });
      }
      setBody("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg h-[560px] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <DialogTitle className="font-display text-lg flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#005C7A]" /> {title || t("chat.title")}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#F9F8F6]">
          {messages.length === 0 && <p className="text-center text-sm text-muted-foreground mt-8">{t("chat.empty")}</p>}
          {messages.map((m) => {
            const mine = m.from_user_id === user?.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#005C7A] text-white" : "bg-white border border-border"}`} data-testid={`msg-${m.message_id}`}>
                  {!mine && <div className="text-[10px] font-semibold opacity-70 mb-0.5">{m.from_name}</div>}
                  <div className="whitespace-pre-line leading-snug">{m.body}</div>
                  <div className={`text-[10px] mt-1 ${mine ? "opacity-70" : "text-muted-foreground"}`}>{m.created_at?.slice(11, 16)}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        <div className="border-t border-border p-3 flex gap-2 bg-white">
          <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("chat.placeholder")}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            data-testid="chat-input" />
          <Button onClick={send} disabled={busy} className="rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid="chat-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
